import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import { getLocalTodayYMD } from "@/lib/dateOnly";
import { getTruckRoute } from "@/lib/mapbox";
import { bumpTemplateUsage, type LRDirectLoadTemplate } from "@/hooks/useLRDirectLoadTemplates";

/**
 * LR Direct — instant post from a saved posting template.
 *
 * Templates carry NO date fields, so there is zero stale-date risk:
 * pickup_date is always TODAY (local YMD, never UTC-derived) and
 * bid_deadline is derived from it (today 17:00 local, floored at now + 6h).
 *
 * LR Direct-only. Never imports from the LaneRunner ATLAS hub.
 */

export interface InstantPostResult {
  loadId: string;
  postType: "load" | "quote";
  pickupDate: string;
  visibility: LRDirectLoadTemplate["visibility_mode"];
  lane: string;
}

/** Today 17:00 local, but never sooner than now + 6h (mirrors the DB trigger). */
function deriveBidDeadline(pickupYMD: string): string {
  const [y, m, d] = pickupYMD.split("-").map(Number);
  const atFive = new Date(y, m - 1, d, 17, 0, 0, 0).getTime();
  const floor = Date.now() + 6 * 60 * 60 * 1000;
  return new Date(Math.max(atFive, floor)).toISOString();
}

export function useInstantPostFromTemplate() {
  const qc = useQueryClient();

  return useMutation<InstantPostResult, Error, { templateId: string; userId: string; isDemo?: boolean }>({
    mutationFn: async ({ templateId, userId, isDemo }) => {
      // 1. Load the template
      const { data: tplRow, error: tplErr } = await supabase
        .from("lr_direct_load_templates" as any)
        .select("*")
        .eq("id", templateId)
        .maybeSingle();
      if (tplErr) throw tplErr;
      if (!tplRow) throw new Error("Template not found. It may have been deleted.");
      const t = tplRow as unknown as LRDirectLoadTemplate;

      if (!t.origin_city || !t.origin_state || !t.destination_city || !t.destination_state) {
        throw new Error("This template is missing its lane. Open it on the post form to complete it.");
      }

      const postType: "load" | "quote" = t.post_type === "quote" ? "quote" : "load";
      const visibility = t.visibility_mode || "open_market";
      const isPrivate = visibility === "private_only" || visibility === "both";

      // 2. Company + pickup date (TODAY) + bid deadline
      const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: userId });
      const companyId = (userCompanies as string[] | null)?.[0] || null;

      const pickupDate = getLocalTodayYMD();
      const bidDeadline = deriveBidDeadline(pickupDate);

      // 3. Private/both → every accepted network member must be able to bid
      let maxBidders = t.max_bidders ?? 5;
      if (isPrivate && t.private_network_id) {
        const { count } = await supabase
          .from("shipper_network_brokers" as any)
          .select("id", { count: "exact", head: true })
          .eq("network_id", t.private_network_id)
          .eq("status", "accepted")
          .is("removed_at", null);
        maxBidders = Math.max(count ?? 0, maxBidders, 1);
      }

      // 4. Best-effort mileage from the template coordinates (never blocks the post)
      let totalMiles: number | null = null;
      if (t.origin_latitude && t.origin_longitude && t.destination_latitude && t.destination_longitude) {
        try {
          const r = await getTruckRoute(
            [Number(t.origin_longitude), Number(t.origin_latitude)],
            [Number(t.destination_longitude), Number(t.destination_latitude)],
          );
          if (r?.distance) totalMiles = Math.round(r.distance);
        } catch (e) {
          console.warn("[instantPost] mileage compute failed", e);
        }
      }

      const payload: Record<string, any> = {
        load_number: generateLoadNumber(),
        created_by: userId,
        company_id: companyId,
        status: "posted",
        is_demo: isDemo === true,
        origin_name: t.origin_name || t.origin_city,
        origin_address: t.origin_address || `${t.origin_city}, ${t.origin_state}`,
        origin_city: t.origin_city,
        origin_state: t.origin_state,
        origin_country: t.origin_country || "US",
        origin_postal_code: t.origin_postal_code || null,
        origin_latitude: t.origin_latitude ?? null,
        origin_longitude: t.origin_longitude ?? null,
        destination_name: t.destination_name || t.destination_city,
        destination_address: t.destination_address || `${t.destination_city}, ${t.destination_state}`,
        destination_city: t.destination_city,
        destination_state: t.destination_state,
        destination_country: t.destination_country || "US",
        destination_postal_code: t.destination_postal_code || null,
        destination_latitude: t.destination_latitude ?? null,
        destination_longitude: t.destination_longitude ?? null,
        pickup_date: pickupDate,
        delivery_date: null,
        equipment_type: t.equipment_type || "van",
        weight_lbs: t.weight_lbs ?? null,
        length_ft: t.length_ft ?? null,
        load_type: t.load_type || "FTL",
        load_nature: (t as any).load_nature || "spot",
        commodity: t.commodity || null,
        special_instructions: t.special_instructions || null,
        total_miles: totalMiles,
        target_rate_min: t.target_rate_min ?? null,
        target_rate_max: t.target_rate_max ?? null,
        max_bidders: maxBidders,
        bid_deadline: bidDeadline,
        priority_tag: t.priority_tag || "standard",
        shipper_notes_to_brokers: t.shipper_notes || null,
        posted_at: new Date().toISOString(),
        appointment_type: t.appointment_type || "fcfs",
        handling_requirements: t.handling_requirements ?? [],
        visibility_mode: visibility,
        post_type: postType,
        private_network_id: isPrivate ? (t.private_network_id ?? null) : null,
      };

      // 5. Insert with the silent-write guard: a returned row is the only
      // proof the write landed. A 0-row insert (RLS with_check mismatch)
      // must surface as an error, never a fake success.
      const { data: newLoad, error } = await supabase
        .from("direct_loads" as any)
        .insert(payload)
        .select("id")
        .single();
      if (error) throw error;
      const loadId = (newLoad as any)?.id as string | undefined;
      if (!loadId) throw new Error("The post was not saved. Please try again.");

      // 6. Stops — use every stop the template saved (all pickups + drops, in
      // order). Older templates without a stop list fall back to origin+dest.
      const tplStops: any[] = Array.isArray((t as any).stops) ? (t as any).stops : [];
      const ordered = [
        ...tplStops.filter((s) => s.stop_type === "pickup"),
        ...tplStops.filter((s) => s.stop_type !== "pickup"),
      ].filter((s) => s.city && s.state);

      const stopRows = ordered.length > 0
        ? ordered.map((s, i) => ({
            direct_load_id: loadId,
            stop_number: i + 1,
            stop_type: s.stop_type === "pickup" ? "pickup" : "delivery",
            name: s.name || "",
            address: s.address || "",
            city: s.city,
            state: s.state,
            postal_code: s.postal_code || null,
            country: s.country || "US",
            latitude: s.latitude ?? null,
            longitude: s.longitude ?? null,
            scheduled_date: pickupDate,
            scheduled_time_start: s.pickup_hours || null,
            scheduled_time_end: s.dropoff_hours || null,
            instructions: s.instructions || null,
          }))
        : [
        {
          direct_load_id: loadId,
          stop_number: 1,
          stop_type: "pickup",
          name: t.origin_name || "",
          address: t.origin_address || "",
          city: t.origin_city,
          state: t.origin_state,
          postal_code: t.origin_postal_code || null,
          country: t.origin_country || "US",
          latitude: t.origin_latitude ?? null,
          longitude: t.origin_longitude ?? null,
          scheduled_date: pickupDate,
        },
        {
          direct_load_id: loadId,
          stop_number: 2,
          stop_type: "delivery",
          name: t.destination_name || "",
          address: t.destination_address || "",
          city: t.destination_city,
          state: t.destination_state,
          postal_code: t.destination_postal_code || null,
          country: t.destination_country || "US",
          latitude: t.destination_latitude ?? null,
          longitude: t.destination_longitude ?? null,
          scheduled_date: pickupDate,
        },
      ];

      const { data: insertedStops, error: stopsErr } = await supabase
        .from("direct_load_stops" as any)
        .insert(stopRows)
        .select("id");
      // Never swallow: a failed stop insert must surface, otherwise the user
      // believes the post succeeded while the load has no stops.
      if (stopsErr) throw new Error(`Load posted but stops failed to save: ${stopsErr.message}`);
      if ((insertedStops?.length ?? 0) !== stopRows.length) {
        throw new Error("Load posted but some stops did not save. Re-open the load and check the stop list.");
      }

      // 7. Usage counter (display only)
      try {
        await bumpTemplateUsage(templateId);
      } catch (e) {
        console.warn("[instantPost] usage bump failed", e);
      }

      // 8. Same broker fan-out as the post form
      if (postType === "quote") {
        supabase.functions
          .invoke("broadcast-new-quote-request", { body: { direct_load_id: loadId } })
          .catch((e) => console.warn("broadcast-new-quote-request failed", e));
      } else {
        if (isPrivate) {
          supabase.functions
            .invoke("broadcast-new-private-load", { body: { direct_load_id: loadId } })
            .catch((e) => console.warn("broadcast-new-private-load failed", e));
        }
        if (visibility === "open_market" || visibility === "both") {
          supabase.functions
            .invoke("broadcast-new-open-load", { body: { direct_load_id: loadId } })
            .catch((e) => console.warn("broadcast-new-open-load failed", e));
        }
      }

      // ATLAS load analysis (bid loads only) — cached on the load row.
      if (postType !== "quote") {
        supabase.functions
          .invoke("atlas-direct-load-analysis", { body: { direct_load_id: loadId } })
          .catch((e) => console.warn("atlas-direct-load-analysis failed", e));
      }



      return {
        loadId,
        postType,
        pickupDate,
        visibility,
        lane: `${t.origin_city}, ${t.origin_state} → ${t.destination_city}, ${t.destination_state}`,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lr-direct-load-templates"] });
      qc.invalidateQueries({ queryKey: ["lr-direct-my-loads"] });
      qc.invalidateQueries({ queryKey: ["direct-quote-requests"] });
    },
  });
}
